// Ranked Arena: automatic 1v1 team battles on the hunt's combat math, a
// weekly rating ladder and its prizes. Pure rules shared by server, client
// and tests; the queue and saving live in server/arena.ts.
import { arenaPower } from './power.ts';
import { beatMs, type Fighter } from './combat.ts';
import type { Relic } from './items.ts';
import type { HeldItem } from './held.ts';
import type { BoardRow, Reach } from './board.ts';
import type { Rolls } from './rolls.ts';
import type { Rng } from './rng.ts';
import { MOVE } from './moves.ts';
import { PVP, fightLevel, firstBeat, oneOfEach, openingCooldowns, overtimeMult, pvpCast, pvpFighter, type PvpMode } from './pvp.ts';
import type { RankedDTO } from './ranked.ts';
import { SYNERGY_SWITCH, synergyMods, withSynergy, type SynergyOpts } from './synergy.ts';

export const ARENA = {
  unlockLevel: 150,
  teamMax: 5,
  lineups: 5,
  placementMatches: 5,
  /** AR can't pass this during placement. */
  placementCap: 299,
  /** Wait after a match before the next one. */
  betweenMatchesMs: 20_000,
  /** The same opponent is not drawn again within this long. */
  sameOpponentMs: 10 * 60_000,
  /** Levels count in full up to here; above it a creature fights at levelFull × (level/levelFull)^levelExp (shared/pvp.ts). */
  levelFull: PVP.knee,
  levelExp: PVP.levelExp.arena,
  /** From here all damage doubles every 10 s (shared/pvp.ts overtime). */
  overtimeMs: PVP.overtimeMs,
  /** A battle still running after this long goes to the side with more creatures standing, then more HP left (share of its total), then the defender. */
  battleCapMs: PVP.capMs,
  /** Loss points are scaled by this. */
  lossMult: 0.7,
  /** Master and Champion need this AR and a ladder place in their range. */
  masterAr: 1500,
  championSlots: 20,
  masterSlots: 50,
  /** Prizes go to the top 100 who played a match in the last 24 h of the season. */
  prizeSlots: 100,
  prizeActiveMs: 24 * 3_600_000,
  /** ...and who played at least this many queued matches that season (placement included) and are above 0 AR. */
  prizeMinMatches: 10,
  /** A win over the same trainer gives AR at most this many times in 24 hours. */
  gainsPerOpponentDay: 3,
  /**
   * A win over a much weaker line-up gives less: full AR when the loser's
   * line-up has at least weakFull of the winner's Power (Arena), none at weakZero or
   * below, in proportion between.
   */
  weakFull: 0.6,
  weakZero: 0.3,
  /** The matchmaker's AR-sorted list of possible opponents is rebuilt at least this often. */
  candidatesMs: 60_000,
} as const;

/**
 * The level a creature fights at in the arena: its own level up to 150, then
 * 150 × (level / 150)^0.35, so stats (which grow in step with level) are
 * compressed above 150. Lv 300 fights as 191, Lv 1,000 as 291.
 */
export function arenaLevel(level: number): number {
  return fightLevel(level, 'arena');
}

// ---------- tiers ----------
export type TierId = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'master' | 'champion';
export const TIERS: { id: TierId; name: string; min: number; k: number }[] = [
  { id: 'bronze', name: 'Bronze', min: 0, k: 32 },
  { id: 'silver', name: 'Silver', min: 300, k: 32 },
  { id: 'gold', name: 'Gold', min: 600, k: 26 },
  { id: 'platinum', name: 'Platinum', min: 900, k: 26 },
  { id: 'diamond', name: 'Diamond', min: 1200, k: 22 },
];
export const TOP_TIERS = { master: { name: 'Master', k: 18 }, champion: { name: 'Champion', k: 18 } } as const;
const DIVS = ['I', 'II', 'III'];

export interface TierInfo {
  id: TierId;
  /** Display name, e.g. "Gold II", "Master", "Diamond III". */
  label: string;
  k: number;
  /** AR 1500+ without a Master or Champion place on the ladder. */
  awaiting?: boolean;
}

/**
 * The tier for an AR. Each tier below Master spans 300 AR in three divisions
 * of 100 (I is the entry, III the top). From 1500 AR, ladder places 1-20 are
 * Champion and 21-50 Master; anyone else at 1500+ shows Diamond III, awaiting
 * a place. rank: the ladder place (1-based), or 0 when not ranked.
 */
export function tierOf(ar: number, rank = 0): TierInfo {
  if (ar >= ARENA.masterAr) {
    if (rank >= 1 && rank <= ARENA.championSlots) return { id: 'champion', label: 'Champion', k: TOP_TIERS.champion.k };
    if (rank >= 1 && rank <= ARENA.masterSlots) return { id: 'master', label: 'Master', k: TOP_TIERS.master.k };
    return { id: 'diamond', label: 'Diamond III', k: TIERS[4].k, awaiting: true };
  }
  const i = Math.max(0, Math.min(TIERS.length - 1, Math.floor(Math.max(0, ar) / 300)));
  const t = TIERS[i];
  const div = Math.min(2, Math.floor((Math.max(0, ar) - t.min) / 100));
  return { id: t.id, label: `${t.name} ${DIVS[div]}`, k: t.k };
}

/** The lowest AR of the tier this AR is in (0, 300, ... 1200, or 1500 from Master up). */
export function tierFloor(ar: number): number {
  if (ar >= ARENA.masterAr) return ARENA.masterAr;
  return Math.floor(Math.max(0, ar) / 300) * 300;
}

// ---------- points ----------
/** Expected score of a player at `a` against one at `b`. */
export function expected(a: number, b: number): number {
  return 1 / (1 + Math.pow(10, (b - a) / 400));
}

/** AR change for the player at `a`: K is the lower of the two players' tier K. */
export function pointsFor(a: number, b: number, kA: number, kB: number, win: boolean): number {
  const k = Math.min(kA, kB);
  const e = expected(a, b);
  return win ? Math.max(1, Math.round(k * (1 - e))) : Math.min(-1, Math.round(k * (0 - e) * ARENA.lossMult));
}

/** The arena record a player carries through a season. */
export interface ArenaStanding {
  ar: number;
  /** Matches played this season (queued ones; defenses don't count). */
  matches: number;
  wins: number;
  losses: number;
  /** The tier floor whose one free loss is still unspent, or 0. */
  shield: number;
  /** Highest tier floor reached this season. */
  best: number;
}

export const freshStanding = (): ArenaStanding => ({ ar: 0, matches: 0, wins: 0, losses: 0, shield: 0, best: 0 });

/**
 * Apply one match result. Placement (the first five matches) keeps AR at
 * 299 or below. The first time a season reaches a new tier, its floor gets a
 * shield: one loss that would drop below that floor leaves you on it instead.
 */
export function applyResult(s: ArenaStanding, delta: number, win: boolean): ArenaStanding {
  const out = { ...s, matches: s.matches + 1, wins: s.wins + (win ? 1 : 0), losses: s.losses + (win ? 0 : 1) };
  let ar = Math.max(0, s.ar + delta);
  if (out.matches <= ARENA.placementMatches) ar = Math.min(ar, ARENA.placementCap);
  if (!win && ar < s.ar) {
    const floor = tierFloor(s.ar);
    if (floor > 0 && s.shield === floor && ar < floor) { ar = floor; out.shield = 0; }
  }
  const floor = tierFloor(ar);
  if (floor > out.best) { out.best = floor; out.shield = floor; }
  out.ar = ar;
  return out;
}

// ---------- matchmaking ----------
/** One number for a line-up's strength: the summed Power (Arena) of the creatures that fight (one of each species). */
export function teamPower(team: ArenaCreature[]): number {
  return oneOfEach(team).reduce((n, c) => n + arenaPower(c), 0);
}

/** Share of a win's AR the winner keeps, by the loser's line-up power against the winner's (see ARENA.weakFull). */
export function gainScale(loserPower: number, winnerPower: number): number {
  if (winnerPower <= 0) return 1;
  const r = loserPower / winnerPower;
  return Math.max(0, Math.min(1, (r - ARENA.weakZero) / (ARENA.weakFull - ARENA.weakZero)));
}

/**
 * The AR a win actually gives: none once this opponent has given AR
 * ARENA.gainsPerOpponentDay times in 24 hours, else scaled by gainScale.
 * Losses are never changed.
 */
export function winGain(delta: number, scale: number, gainsToday: number): number {
  if (delta <= 0) return delta;
  if (gainsToday >= ARENA.gainsPerOpponentDay) return 0;
  return Math.round(delta * scale);
}

/** The AR window by time waited: [waited at least, ±AR]. Infinity = anyone. */
export const WINDOW_STEPS: [number, number][] = [
  [0, 25], [2 * 60_000, 50], [5 * 60_000, 100], [15 * 60_000, 150], [30 * 60_000, 250],
  [3_600_000, 400], [2 * 3_600_000, 600], [4 * 3_600_000, 1000], [6 * 3_600_000, Infinity],
];

export function matchWindow(waitedMs: number): number {
  let w = WINDOW_STEPS[0][1];
  for (const [at, ar] of WINDOW_STEPS) if (waitedMs >= at) w = ar;
  return w;
}

export interface Candidate { account: number; ar: number; live: boolean }

/**
 * The opponent for a player: inside the window, not met in the last 10
 * minutes, closest AR first; a live queued player wins a tie with a saved
 * line-up, then the lower account id (so results are repeatable).
 */
export function pickOpponent(me: { account: number; ar: number }, pool: Candidate[], window: number, recent: Set<number>): Candidate | null {
  let best: Candidate | null = null;
  for (const c of pool) {
    if (c.account === me.account || recent.has(c.account)) continue;
    const d = Math.abs(c.ar - me.ar);
    if (d > window) continue;
    if (!best) { best = c; continue; }
    const bd = Math.abs(best.ar - me.ar);
    if (d < bd || (d === bd && (c.live && !best.live || (c.live === best.live && c.account < best.account)))) best = c;
  }
  return best;
}

// ---------- seasons ----------
/** Seasons run a week, from Sunday 03:00 UTC. */
export function seasonStart(now: number): number {
  const d = new Date(now);
  const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 3);
  let start = day - d.getUTCDay() * 86_400_000;
  if (start > now) start -= 7 * 86_400_000;
  return start;
}
export const seasonEnd = (now: number) => seasonStart(now) + 7 * 86_400_000;
/** A season's id: its start date (YYYY-MM-DD, a Sunday). */
export const seasonId = (now: number) => new Date(seasonStart(now)).toISOString().slice(0, 10);

/** Prize bands: [last place in band, diamonds, XP boost hours]. */
export const PRIZES: [number, number, number][] = [
  [1, 50, 24], [3, 25, 16], [10, 15, 12], [20, 8, 8], [50, 3, 4], [100, 0, 2],
];

export function prizeFor(rank: number): { diamonds: number; xpHours: number } | null {
  if (rank < 1) return null;
  for (const [last, diamonds, xpHours] of PRIZES) if (rank <= last) return { diamonds, xpHours };
  return null;
}

/** Diamonds a full ladder pays in one week: the most arena prizes can add. */
export function weeklyPrizeDiamonds(): number {
  let total = 0, prev = 0;
  for (const [last, diamonds] of PRIZES) { total += (last - prev) * diamonds; prev = last; }
  return total;
}

export interface LadderEntry { account: number; ar: number; arAt: number; lastPlayed: number; matches: number }

/** Ladder order: AR, then whoever reached it first. Only players with a match this season. */
export function ladderOrder<T extends LadderEntry>(rows: T[]): T[] {
  return rows.filter((r) => r.matches > 0).sort((a, b) => b.ar - a.ar || a.arAt - b.arAt || a.account - b.account);
}

/** Whether a season's row can earn a prize: enough matches played, above 0 AR, active in its last 24 h. */
export function prizeEligible(r: LadderEntry, end: number): boolean {
  return r.matches >= Math.max(ARENA.placementMatches, ARENA.prizeMinMatches) && r.ar > 0 && r.lastPlayed >= end - ARENA.prizeActiveMs;
}

/** Prize winners for a finished season: the top 100 of those eligible (prizeEligible), ranked among themselves. */
export function prizeWinners<T extends LadderEntry>(rows: T[], end: number): { row: T; rank: number; diamonds: number; xpHours: number }[] {
  const active = ladderOrder(rows).filter((r) => prizeEligible(r, end));
  return active.slice(0, ARENA.prizeSlots).map((row, i) => ({ row, rank: i + 1, ...prizeFor(i + 1)! }));
}

// ---------- battle ----------
export interface ArenaCreature { id: number; species: string; level: number; rolls: Rolls; stars: number; relic: Relic | null; refine?: number[]; tablet?: string; held?: HeldItem | null }

/** row, reach: on the board (shared/board.ts), each creature's row and its reach. */
export interface BattleSide { name: string; team: { id: number; species: string; level: number; arenaLevel: number; maxHp: number; aura: boolean; row?: BoardRow; reach?: Reach }[] }
/**
 * One blow: ms into the fight, side (0 or 1), attacker and target index in their teams, damage, crit, element
 * multiplier, target HP left, and the move id (shared/moves.ts; absent in logs saved before moves existed).
 */
export interface Blow {
  ms: number; s: 0 | 1; a: number; d: number; dmg: number; crit: boolean; mult: number; hp: number; mv?: string;
  /** Held items (shared/held.ts), only when one acted on this blow: damage a Ward Rune's shield took, and the attacker's HP after a Bronze Mirror, Fox Lantern or Mead Horn changed it. */
  sa?: number; ah?: number;
  /** On the board (shared/board.ts): the attacker's and the defender's charge after this moment, 0-100. */
  ca?: number; cd?: number;
}
/**
 * synergy: each side's synergy options when synergies applied to either side (shared/synergy.ts; null: none), so a page
 * can rebuild each side's chips from its species (sideSynergy). Absent when neither side had them.
 */
export interface BattleResult {
  sides: [BattleSide, BattleSide]; log: Blow[]; winner: 0 | 1; ms: number; timedOut: boolean; synergy?: [SynergyOpts | null, SynergyOpts | null];
  /** Fought on the board (shared/pvpfight.ts pvpBattle): its rules version (shared/board.ts BOARD.version). Absent: fronts in order. */
  board?: number;
  /** A boss duel (shared/boss-duel.ts) replayed on the board (client/bossboard.ts): side 1 is this boss alone. */
  boss?: string;
}

export type SynergyChoice = boolean | SynergyOpts;
/**
 * Options for battle(). synergy: true or options = on, false = off, a pair = per side (bench rows); absent = the live
 * switch (SYNERGY_SWITCH) in 'arena' mode, never in 'war'. held: held items (shared/held.ts) on or off; absent = the
 * live switch (HELD_SWITCH). They count in 'arena' mode only either way.
 */
export interface BattleOpts {
  synergy?: SynergyChoice | readonly [SynergyChoice, SynergyChoice]; held?: boolean;
  /**
   * Fronts in order: relic damage counts at this power (absent: PVP.relicExp); tournament games on rules 3-6 replay
   * with 1. On the board the board rules carry it (shared/board.ts BoardRules.relicExp).
   */
  relicExp?: number;
}

/** Each side's synergy options as `opts` says (BattleOpts.synergy; absent: the live switch in 'arena' mode), null for none. */
export function synergyPer(opts: Pick<BattleOpts, 'synergy'>, mode: PvpMode = 'arena'): [SynergyOpts | null, SynergyOpts | null] {
  const syn = opts.synergy ?? (mode === 'arena' && SYNERGY_SWITCH.on);
  const pick = (x: SynergyChoice): SynergyOpts | null => (x === false ? null : x === true ? {} : x);
  return Array.isArray(syn) ? [pick(syn[0]), pick(syn[1])] : [pick(syn as SynergyChoice), pick(syn as SynergyChoice)];
}

/** The synergy options side s fought with, or null when it had none. */
export const sideSynergy = (r: Pick<BattleResult, 'synergy'>, s: 0 | 1): SynergyOpts | null => r.synergy?.[s] ?? null;

/** A creature as it fights in the Arena, Halls and duels (mode 'war': at Guild War levels; held, relicExp: BattleOpts): shared/pvp.ts. */
export function arenaFighter(c: ArenaCreature, mode: PvpMode = 'arena', held?: boolean, relicExp?: number): Fighter {
  return pvpFighter(c, mode, held, undefined, undefined, relicExp);
}

/**
 * Fronts fight one on one, in line-up order, on the PvP ruleset (shared/pvp.ts): moves by the auto-cast rule, HP ×3.3,
 * initiative, fixed opening cooldowns, overtime from 90 s and the 120 s backstop. The rng draws the damage rolls only. A knocked-out creature is replaced
 * by the next; the side with someone standing wins. Only the first of each species fights. Side 1 is the defender.
 * `mode` 'war' fights at Guild War levels (the bench's 1v1 war rows). Synergies (shared/synergy.ts) apply before the
 * fight, per side, as `opts` says (BattleOpts).
 */
export function battle(a: { name: string; team: ArenaCreature[] }, b: { name: string; team: ArenaCreature[] }, rng: Rng, mode: PvpMode = 'arena', opts: BattleOpts = {}): BattleResult {
  const per = synergyPer(opts, mode);
  const teams = [oneOfEach(a.team), oneOfEach(b.team)].map((t, s) => {
    const mods = per[s] ? synergyMods(t, per[s]!) : null;
    return t.map((c, i) => {
      const f = mods ? withSynergy(arenaFighter(c, mode, opts.held, opts.relicExp), mods[i]) : arenaFighter(c, mode, opts.held, opts.relicExp);
      const max = Math.max(1, Math.round(f.stats.hp * PVP.hpMult));
      return { c, f, max, hp: max, next: Infinity, ready: new Map<string, number>(), shield: 0, struck: false };
    });
  });
  const front = [0, 0];
  const log: Blow[] = [];
  let ms = 0;
  const enter = (s: number) => {
    const u = teams[s][front[s]];
    if (!u) return;
    u.ready = openingCooldowns(u.f, ms);
    u.next = ms + firstBeat(u.f, teams[1 - s][front[1 - s]]?.f);
    if (u.f.held?.shield) u.shield = Math.round(u.max * u.f.held.shield);
    u.struck = false;
  };
  enter(0); enter(1);
  // Blows due at the same moment land together (no side owns a tie), so a dead-even pair can knock each other out.
  const dealt = [0, 0];
  while (front[0] < teams[0].length && front[1] < teams[1].length) {
    const n0 = teams[0][front[0]].next, n1 = teams[1][front[1]].next;
    const now = Math.min(n0, n1);
    if (now > ARENA.battleCapMs) { ms = ARENA.battleCapMs; break; }
    ms = now;
    const strikers = ([0, 1] as const).filter((s) => teams[s][front[s]].next === now);
    // Both casts read the HP before either blow lands.
    const blows = strikers.map((s) => {
      const att = teams[s][front[s]], def = teams[1 - s][front[1 - s]];
      return { s, h: pvpCast(att.f, def.f, att.ready, def.hp, ms, rng) };
    });
    for (const { s, h } of blows) {
      const o = 1 - s;
      const att = teams[s][front[s]], def = teams[o][front[o]];
      // An Oni Mask hits harder under half HP, a Thunder Phial's first blow harder (held items, shared/held.ts).
      const rage = att.f.held?.rage && att.hp * 2 < att.max ? 1 + att.f.held.rage : 1;
      const first = att.f.held?.first && !att.struck ? 1 + att.f.held.first : 1;
      att.struck = true;
      const dmg = Math.max(1, Math.round(h.dmg * overtimeMult(ms) * rage * first));
      const blow: Blow = { ms: Math.round(ms), s, a: front[s], d: front[o], dmg, crit: h.crit, mult: h.mult, hp: def.hp, mv: h.mv };
      let taken = dmg;
      if (def.shield > 0) { const sa = Math.min(def.shield, taken); def.shield -= sa; taken -= sa; blow.sa = sa; }
      const before = def.hp;
      def.hp = Math.max(0, def.hp - taken);
      blow.hp = def.hp;
      dealt[s] += dmg;
      if (att.f.held || def.f.held) {
        const lost = before - def.hp, ah = att.hp;
        if (def.f.held?.reflect && lost > 0 && att.hp > 0) att.hp = Math.max(0, att.hp - Math.round(lost * def.f.held.reflect));
        if (att.f.held?.drain && lost > 0 && att.hp > 0) att.hp = Math.min(att.max, att.hp + Math.round(lost * att.f.held.drain));
        if (att.f.held?.healKo && def.hp <= 0 && before > 0 && att.hp > 0) att.hp = Math.min(att.max, att.hp + Math.round(att.max * att.f.held.healKo));
        if (att.hp !== ah) blow.ah = att.hp;
      }
      log.push(blow);
      att.next = ms + beatMs(att.f);
    }
    const down = ([0, 1] as const).filter((s) => teams[s][front[s]].hp <= 0);
    for (const s of down) front[s]++;
    for (const s of down) enter(s);
  }
  const timedOut = front[0] < teams[0].length && front[1] < teams[1].length;
  let winner: 0 | 1;
  const out = [front[0] >= teams[0].length, front[1] >= teams[1].length];
  // Both last creatures fell on the same blow: the side that dealt more damage wins, then the defender.
  if (out[0] && out[1]) winner = dealt[0] > dealt[1] ? 0 : 1;
  else if (!timedOut) winner = out[0] ? 1 : 0;
  else {
    const alive = teams.map((t, i) => t.length - front[i]);
    const share = teams.map((t) => t.reduce((x, y) => x + y.hp, 0) / t.reduce((x, y) => x + y.max, 0));
    winner = alive[0] !== alive[1] ? (alive[0] > alive[1] ? 0 : 1) : share[0] > share[1] ? 0 : 1;
  }
  const side = (name: string, t: typeof teams[0]): BattleSide => ({
    name, team: t.map(({ c, f, max }) => ({ id: c.id, species: c.species, level: c.level, arenaLevel: f.level, maxHp: max, aura: c.rolls.aura })),
  });
  return { sides: [side(a.name, teams[0]), side(b.name, teams[1])], log, winner, ms: Math.round(ms), timedOut, ...(per[0] || per[1] ? { synergy: per } : {}) };
}

/** Signatures cast in a battle (the bench's sigsPer5v5). */
export const signaturesIn = (r: BattleResult) => r.log.filter((x) => x.mv && MOVE[x.mv]?.slot === 'signature').length;

// ---------- messages ----------
/** rows: front or back per creature, in the order of ids (the board, shared/pvpfight.ts); absent: defaultRows. */
export interface ArenaLineup { name: string; ids: number[]; w: number; l: number; rows?: BoardRow[] }
export interface MatchSummary {
  id: number; ts: number; opp: string; oppTier: string; win: boolean; delta: number; ar: number;
  /** Both players were queued; otherwise the opponent's saved line-up fought. */
  live: boolean;
  /** Someone else queued into your saved line-up (your AR doesn't move). */
  defense: boolean;
}
export interface LadderRow { rank: number; name: string; ar: number; tier: string; tierId: TierId; outfit: string; me?: boolean; w: number; l: number }
export interface ArenaDTO {
  unlocked: boolean;
  season: { id: string; endsAt: number };
  me: {
    ar: number; tier: TierInfo; rank: number; matches: number; wins: number; losses: number;
    placementLeft: number; shield: number; queued: boolean;
    /** When the next match may start (the 20 s pause). */
    nextAt: number;
    /** The AR window now (null = anyone), and since when you have waited. */
    window: number | null; waitingSince: number;
  };
  lineups: ArenaLineup[];
  /** Index into lineups, or -1 for the hunt team. */
  active: number;
  ladder: LadderRow[];
  /** Your row when you are below the shown ladder. */
  mine?: LadderRow;
  recent: MatchSummary[];
  notice?: { season: string; rank: number; diamonds: number; xpHours: number };
  /** Set while ranked seasons are on (shared/ranked.ts): the page shows this ladder, and `unlocked` is from Lv 40. */
  ranked?: RankedDTO;
}
/** A line-up as others see it: the creatures, never their order. */
export interface ArenaPeek { name: string; ar: number; tier: string; team: { species: string; level: number; arenaLevel: number; aura: boolean; skin?: string }[] }
